import { filestackPath, safeParseJSON } from 'lib/helpers/utility';

const PRELOAD_TIMEOUT_MS = 8000;

const dataUrlByImageId = new Map();
const inFlightByImageId = new Map();

export const getImageFileId = (image) => {
  const practiceImage = safeParseJSON(image, image);
  const finalImageId =
    typeof practiceImage === 'string' && !practiceImage.includes('{')
      ? practiceImage
      : practiceImage?.jpg || practiceImage?.png;

  return finalImageId;
};

const readAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * @param {string} imageId
 * @returns {string|null} the preloaded data URI, or null when it was never loaded
 */
export const getPreloadedHeaderImage = (imageId) => dataUrlByImageId.get(imageId) || null;

/**
 * Resolves a practice header photo to a data URI before it reaches jsPDF.
 *
 * jsPDF's getImageProperties/addImage fetch a URL through browserRequest, which
 * defaults `sync` to true and so issues a blocking XMLHttpRequest — a slow or
 * unreachable image freezes the tab for the whole request. Handing jsPDF an
 * already-decoded data URI keeps it off the network entirely.
 *
 * Resolves to null when the image cannot be loaded; that result is cached so a
 * broken image costs the timeout once rather than on every export.
 *
 * @param {string|Object} image - practice.header_photo, raw or JSON-encoded
 * @returns {Promise<string|null>}
 */
export const preloadHeaderImage = async (image) => {
  const imageId = getImageFileId(image);
  if (!imageId) return null;
  if (dataUrlByImageId.has(imageId)) return getPreloadedHeaderImage(imageId);
  if (inFlightByImageId.has(imageId)) return inFlightByImageId.get(imageId);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PRELOAD_TIMEOUT_MS);

  const request = fetch(filestackPath(imageId), {
    credentials: 'include',
    signal: controller.signal
  })
    .then((response) => (response.ok ? response.blob() : null))
    .then((blob) => (blob ? readAsDataUrl(blob) : null))
    .catch(() => null)
    .then((dataUrl) => {
      dataUrlByImageId.set(imageId, dataUrl);
      return dataUrl;
    })
    .finally(() => {
      clearTimeout(timeout);
      inFlightByImageId.delete(imageId);
    });

  inFlightByImageId.set(imageId, request);

  return request;
};
